import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { LOGO } from "@/lib/assets";

const NAV = [
  { label: "Shop", to: "/shop" },
  { label: "Consultation", to: "/consultation" },
  { label: "AI Analysis", to: "/analysis" },
  { label: "Journal", to: "/blog" },
];

export default function Navbar() {
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
          scrolled
            ? "bg-cream/80 backdrop-blur-xl border-b border-black/10 py-4"
            : "bg-transparent py-6"
        }`}
      >
        <div className="mx-auto max-w-[1500px] px-5 md:px-10 flex items-center justify-between">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center"
          >
            <img src={LOGO} alt="HairPrive" className="h-40 md:h-48 w-auto object-contain" />
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className="link-underline font-sans font-semibold uppercase tracking-[0.2em] text-[14px] text-obsidian hover:text-amber-deep"
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-5">
            <button
              data-testid="cart-open-btn"
              onClick={() => setOpen(true)}
              className="relative text-obsidian hover:text-amber transition-colors"
              aria-label="Open cart"
            >
              <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
              {count > 0 && (
                <span
                  data-testid="cart-count"
                  className="absolute -top-2 -right-2 bg-amber text-cream text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-sans"
                >
                  {count}
                </span>
              )}
            </button>
            <button
              className="md:hidden text-obsidian"
              onClick={() => setMenu(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <div
        className={`fixed inset-0 z-[60] bg-obsidian text-cream md:hidden grain panel-right ${menu ? "open" : ""}`}
      >
        <div className="relative z-10 flex flex-col h-full p-8">
          <div className="flex justify-between items-center">
            <img src={LOGO} alt="HairPrive" className="h-16 w-auto object-contain" style={{ filter: "brightness(0) invert(1)" }} />
            <button onClick={() => setMenu(false)} data-testid="mobile-menu-close" aria-label="Close menu">
              <X className="w-7 h-7" strokeWidth={1.5} />
            </button>
          </div>
          <nav className="mt-16 flex flex-col gap-8">
            {NAV.map((n) => (
              <button
                key={n.to}
                onClick={() => { setMenu(false); navigate(n.to); }}
                className="text-left font-serif text-4xl"
              >
                {n.label}
              </button>
            ))}
          </nav>
        </div>
      </div>
    </>
  );
}
